import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../features/auth/context/AuthContext";
import Landing from "../features/public/pages/Landing";
import Login from "../features/public/pages/Login";
import Register from "../features/public/pages/Register";
import ForgotPassword from "../features/public/pages/ForgotPassword";
import PublicTracking from "../features/public/pages/PublicTracking";
import CustomerHome from "../features/customer/pages/CustomerHome";
import MyItems from "../features/customer/pages/MyItems";
import MyAddresses from "../features/customer/pages/MyAddresses";
import PayNow from "../features/customer/pages/PayNow";
import TrackShipment from "../features/customer/pages/TrackShipment";
import MyReceipts from "../features/customer/pages/MyReceipts";
import ReferEarn from "../features/customer/pages/ReferEarn";
import LoyaltyPage from "../features/customer/pages/LoyaltyPage";
import ChatPage from "../features/customer/pages/ChatPage";
import ProfilePage from "../features/customer/pages/ProfilePage";
import WelcomeAssistant from "../features/customer/pages/WelcomeAssistant";
import AdminHome from "../features/admin/pages/AdminHome";
import AdminUsers from "../features/admin/pages/AdminUsers";
import AdminUpload from "../features/admin/pages/AdminUpload";
import AdminTransactions from "../features/admin/pages/AdminTransactions";
import AdminPricing from "../features/admin/pages/AdminPricing";
import AdminAddresses from "../features/admin/pages/AdminAddresses";
import AdminIntegrations from "../features/admin/pages/AdminIntegrations";
import AdminPromotions from "../features/admin/pages/AdminPromotions";
import AdminChats from "../features/admin/pages/AdminChats";
import AdminAnalytics from "../features/admin/pages/AdminAnalytics";
import AdminBatches from "../features/admin/pages/AdminBatches";
import AdminShipmentUpdates from "../features/admin/pages/AdminShipmentUpdates";
import AdminUnmatched from "../features/admin/pages/AdminUnmatched";

function getCustomerDefaultPath(user) {
  return user?.onboarding_completed ? "/home" : "/welcome";
}

function PrivateRoute({ children, adminOnly = false, onboardingOnly = false }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="loading-center">
        <div className="spinner spinner-dark" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin")
    return <Navigate to="/home" replace />;
  if (onboardingOnly) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.onboarding_completed) return <Navigate to="/home" replace />;
    return children;
  }
  if (user.role !== "admin" && !user.onboarding_completed)
    return <Navigate to="/welcome" replace />;
  return children;
}

export default function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/login"
        element={
          user ? (
            <Navigate
              to={
                user.role === "admin" ? "/admin" : getCustomerDefaultPath(user)
              }
            />
          ) : (
            <Login />
          )
        }
      />
      <Route
        path="/register"
        element={
          user ? (
            <Navigate
              to={
                user.role === "admin" ? "/admin" : getCustomerDefaultPath(user)
              }
            />
          ) : (
            <Register />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          user ? (
            <Navigate
              to={
                user.role === "admin" ? "/admin" : getCustomerDefaultPath(user)
              }
            />
          ) : (
            <ForgotPassword />
          )
        }
      />
      <Route path="/track/:code" element={<PublicTracking />} />

      <Route
        path="/welcome"
        element={
          <PrivateRoute onboardingOnly>
            <WelcomeAssistant />
          </PrivateRoute>
        }
      />
      <Route
        path="/home"
        element={
          <PrivateRoute>
            <CustomerHome />
          </PrivateRoute>
        }
      />
      <Route
        path="/items"
        element={
          <PrivateRoute>
            <MyItems />
          </PrivateRoute>
        }
      />
      <Route
        path="/addresses"
        element={
          <PrivateRoute>
            <MyAddresses />
          </PrivateRoute>
        }
      />
      <Route
        path="/pay"
        element={
          <PrivateRoute>
            <PayNow />
          </PrivateRoute>
        }
      />
      <Route
        path="/track"
        element={
          <PrivateRoute>
            <TrackShipment />
          </PrivateRoute>
        }
      />
      <Route
        path="/receipts"
        element={
          <PrivateRoute>
            <MyReceipts />
          </PrivateRoute>
        }
      />
      <Route
        path="/refer"
        element={
          <PrivateRoute>
            <ReferEarn />
          </PrivateRoute>
        }
      />
      <Route
        path="/loyalty"
        element={
          <PrivateRoute>
            <LoyaltyPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <PrivateRoute>
            <ChatPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <ProfilePage />
          </PrivateRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <PrivateRoute adminOnly>
            <AdminHome />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <PrivateRoute adminOnly>
            <AdminUsers />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/upload"
        element={
          <PrivateRoute adminOnly>
            <AdminUpload />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/transactions"
        element={
          <PrivateRoute adminOnly>
            <AdminTransactions />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/pricing"
        element={
          <PrivateRoute adminOnly>
            <AdminPricing />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/addresses"
        element={
          <PrivateRoute adminOnly>
            <AdminAddresses />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <PrivateRoute adminOnly>
            <AdminIntegrations />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/promotions"
        element={
          <PrivateRoute adminOnly>
            <AdminPromotions />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/chats"
        element={
          <PrivateRoute adminOnly>
            <AdminChats />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <PrivateRoute adminOnly>
            <AdminAnalytics />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/batches"
        element={
          <PrivateRoute adminOnly>
            <AdminBatches />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/updates"
        element={
          <PrivateRoute adminOnly>
            <AdminShipmentUpdates />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/unmatched"
        element={
          <PrivateRoute adminOnly>
            <AdminUnmatched />
          </PrivateRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}
